import {
  IconAlertCircle,
  IconAuditLog,
  IconDomains,
  IconProducts,
  IconQrCode,
  IconRefresh,
} from '@myplayer/ui';
import { type ReactElement } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  EmptyState,
  ErrorBanner,
  SegmentedControl,
  Skeleton,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildOverviewProductContextMenuItems } from './context-menu-items.js';
import {
  formatShortUtcDayLabel,
  type AggregatedOverviewAnalytics,
  type OverviewTimeWindow,
} from './overview-analytics-helpers.js';

const WINDOW_OPTIONS: readonly { readonly days: OverviewTimeWindow; readonly label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export interface OverviewAnalyticsSectionProps {
  readonly analytics: AggregatedOverviewAnalytics;
  readonly windowDays: OverviewTimeWindow;
  readonly onChangeWindowDays: (days: OverviewTimeWindow) => void;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onDownloadQrClick?: () => void;
  readonly showWorkspaceBadges?: boolean;
  readonly onSelectTopProduct?: (identifierKey: string, workspaceId?: string) => void;
}

export function OverviewAnalyticsSection({
  analytics,
  windowDays,
  onChangeWindowDays,
  loading,
  error,
  onRetry,
  showWorkspaceBadges = false,
  onSelectTopProduct,
}: OverviewAnalyticsSectionProps): ReactElement {
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h2 className="text-section-heading">Scan performance</h2>
          <span className="text-[12px] text-fg-secondary tabular-nums">
            ({analytics.fromDay} to {analytics.toDay} UTC)
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            role="group"
            ariaLabel="Scan analytics time range"
            data-testid="overview-window-control"
            value={windowDays}
            onChange={onChangeWindowDays}
            items={WINDOW_OPTIONS.map((opt) => ({
              value: opt.days,
              label: opt.label,
              testId: `overview-window-${String(opt.days)}`,
            }))}
          />
          <Button
            type="button"
            size="icon"
            variant="secondary"
            aria-label="Refresh scan analytics"
            title="Refresh scan analytics"
            onClick={onRetry}
          >
            <IconRefresh />
          </Button>
        </div>
      </div>

      {error !== null && (
        <ErrorBanner
          title="Could not load scan analytics"
          message={error}
          nextStep="Check your connection and retry."
          onRetry={onRetry}
        />
      )}

      {loading ? (
        <div
          data-testid="overview-analytics-skeleton"
          className="grid grid-cols-1 gap-4 lg:grid-cols-3"
        >
          <Skeleton className="h-56 w-full lg:col-span-3" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : analytics.totalScans === 0 ? (
        <EmptyState
          data-testid="overview-no-scans-state"
          icon={<IconQrCode />}
          title="No scans yet"
          description={`No QR code scans were recorded in the last ${String(windowDays)} days. Download a product QR code and test-scan it with your phone camera—daily metrics populate asynchronously via the scan-events pipeline.`}
        />
      ) : (
        <div className="space-y-4">
          <Card data-testid="overview-scans-chart">
            <CardHeader>
              <div className="flex min-w-0 items-center gap-3">
                <CardIcon>
                  <IconAuditLog />
                </CardIcon>
                <CardTitle>Scans over time ({windowDays} days)</CardTitle>
              </div>
              {analytics.peakDay !== null && (
                <Tag variant="outline">
                  Peak: {analytics.peakDay.scans.toLocaleString()} on{' '}
                  {formatShortUtcDayLabel(analytics.peakDay.day)}
                </Tag>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <CardDescription>
                Daily QR code scans across all products in this workspace
              </CardDescription>
              <div aria-hidden="true" className="flex h-44 items-end gap-1.5 pt-2">
                {analytics.series.map((pt) => (
                  <div
                    key={pt.day}
                    className="group relative flex h-full flex-1 flex-col items-center justify-end"
                    title={`${pt.shortLabel}: ${pt.scans.toLocaleString()} scans`}
                  >
                    <div
                      className="w-full rounded-t-[4px] bg-brand transition-opacity group-hover:opacity-85"
                      style={{ height: `${String(pt.heightPct)}%` }}
                    />
                  </div>
                ))}
              </div>
              <div
                aria-hidden="true"
                className="mt-2 flex items-center justify-between border-t border-subtle pt-2 text-[11px] text-fg-muted tabular-nums"
              >
                <span>{analytics.series[0]?.shortLabel}</span>
                <span>{analytics.series[Math.floor(analytics.series.length / 2)]?.shortLabel}</span>
                <span>{analytics.series[analytics.series.length - 1]?.shortLabel}</span>
              </div>
              <div className="relative h-0 w-0 overflow-hidden">
                <table className="sr-only" data-testid="overview-scans-sr-table">
                  <caption>
                    Daily QR code scans over the last {windowDays} days ({analytics.fromDay} to{' '}
                    {analytics.toDay} UTC)
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Date (UTC)</th>
                      <th scope="col">Scans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.series.map((pt) => (
                      <tr key={pt.day}>
                        <td>{pt.day}</td>
                        <td>{pt.scans}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card data-testid="overview-top-products" className="h-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CardIcon>
                    <IconProducts />
                  </CardIcon>
                  <CardTitle>Top products</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription>Most scanned products in period</CardDescription>
                {analytics.topProducts.map((item) => (
                  <div
                    key={item.identifierKey}
                    data-testid={`top-product-item-${item.identifierKey}`}
                    onClick={
                      onSelectTopProduct
                        ? () => onSelectTopProduct(item.identifierKey, item.workspaceId)
                        : undefined
                    }
                    onContextMenu={bindContextMenu(() =>
                      buildOverviewProductContextMenuItems({
                        identifierKey: item.identifierKey,
                        primaryKey: item.primaryKey,
                        title: item.title,
                        onOpenProduct: onSelectTopProduct
                          ? () => onSelectTopProduct(item.identifierKey, item.workspaceId)
                          : undefined,
                      }),
                    )}
                    className={`space-y-1 ${onSelectTopProduct ? 'cursor-pointer rounded-control p-1 -mx-1 transition-colors hover:bg-surface-subtle' : ''}`}
                  >
                    <div className="flex items-baseline justify-between gap-2 text-[13px]">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate font-medium text-fg">{item.title}</span>
                        {showWorkspaceBadges && item.workspaceName && (
                          <Tag
                            variant="outline"
                            size="sm"
                            data-testid={`top-product-ws-badge-${item.identifierKey}`}
                          >
                            {item.workspaceName}
                          </Tag>
                        )}
                      </div>
                      <span className="shrink-0 font-semibold text-fg tabular-nums">
                        {item.scans.toLocaleString()} ({item.sharePct}%)
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-pill bg-brand-wash">
                      <div
                        className="h-full rounded-pill bg-brand"
                        style={{ width: `${String(Math.max(4, item.sharePct))}%` }}
                      />
                    </div>
                    <div className="font-mono text-[11px] text-fg-muted">
                      GTIN {item.primaryKey}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="overview-top-countries" className="h-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CardIcon>
                    <IconDomains />
                  </CardIcon>
                  <CardTitle>Top countries</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription>Where scanners are located</CardDescription>
                {analytics.topCountries.map((country) => (
                  <div key={country.countryCode} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-2 text-[13px]">
                      <span className="truncate font-medium text-fg">{country.countryLabel}</span>
                      <span className="shrink-0 font-semibold text-fg tabular-nums">
                        {country.scans.toLocaleString()} ({country.sharePct}%)
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-pill bg-brand-wash">
                      <div
                        className="h-full rounded-pill bg-brand"
                        style={{ width: `${String(Math.max(4, country.sharePct))}%` }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="overview-device-split" className="h-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CardIcon>
                    <IconQrCode />
                  </CardIcon>
                  <CardTitle>Device split</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription>Scanner device types</CardDescription>
                {analytics.deviceSplit.length === 0 ? (
                  <p className="flex items-center gap-2 text-[12px] text-fg-secondary">
                    <IconAlertCircle />
                    <span>No device category breakdown recorded for these scans.</span>
                  </p>
                ) : (
                  analytics.deviceSplit.map((dev) => (
                    <div key={dev.category} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-2 text-[13px]">
                        <span className="truncate font-medium text-fg">{dev.label}</span>
                        <span className="shrink-0 font-semibold text-fg tabular-nums">
                          {dev.scans.toLocaleString()} ({dev.sharePct}%)
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-pill bg-brand-wash">
                        <div
                          className="h-full rounded-pill bg-brand"
                          style={{ width: `${String(Math.max(4, dev.sharePct))}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      {contextMenuElement}
    </div>
  );
}
